import type { UserPermissionType } from "../schemas/PermissionsSchemas.ts";
import type { ReportWithDownloadV2Type } from "../schemas/ReportSchemas.ts";
import type { MembershipWithGroupType } from "../schemas/RolesSchemas.ts";
import type {
  CreateUserInput,
  UpdateUserInput,
  UserReturnType,
  UserWithCompaniesType,
  UserWithGroupsType,
} from "../schemas/UserSchemas.ts";
import type {
  CreateOneResponseType,
  FilterObjectType,
  GetListRequestType,
  GetListResponseType,
  GetOneRequestType,
  GetOneResponseType,
  ResourceAdapterType,
  UpdateOneResponseType,
} from "../util/index.ts";
import { fetchAndCollate, getFilterObject } from "../util/index.ts";
import { restAPI } from "./api.ts";

export type UsersCreateOneParamsType = {
  company_id?: string;
  body: CreateUserInput;
};

export type UsersUpdateOneParamsType = {
  id: string;
  data: UpdateUserInput;
};

export const UsersAdapter: UsersAdapterResourceServiceType = {
  /**
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/users/search_users
   */
  getList: async ({ page, pageSize, sortBy, sortDirection, filters }) =>
    fetchAndCollate<UserWithGroupsType>(
      ({ page, pageSize }) =>
        restAPI.nodeAPI.POST<GetListResponseType<UserWithGroupsType>>({
          endpoint: "/users/search",
          body: {
            page,
            page_size: pageSize,
            sort_by: sortBy,
            sort_direction: sortDirection,
            filter: getFilterObject(filters),
          },
        }),
      page,
      pageSize
    ),
  /**
   * Get a user's details
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/users/get_user
   */
  getOne: async ({ id }) =>
    restAPI.nodeAPI
      .GET<UserReturnType>({
        endpoint: `/users/${id}`,
      })
      .then((resp) => ({
        data: resp,
      })),
  getMany: () =>
    Promise.reject({
      success: false,
      error: "Method not implemented",
    }),
  myself: {
    getOne: async () =>
      restAPI.nodeAPI
        .GET<UserWithCompaniesType>({
          endpoint: `/users/myself`,
        })
        .then((resp) => ({
          data: resp,
        })),
  },
  /**
   * Create a user (human or machine). Receives company_id and body (discriminated on type).
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/users/create_user
   */
  createOne: ({ company_id, body }) =>
    restAPI.nodeAPI
      .POST<UserReturnType>({
        endpoint: `/users`,
        body: company_id !== undefined ? { ...body, company_id } : body,
      })
      .then((resp) => ({
        data: resp,
      })),
  createMany: () =>
    Promise.reject({
      success: false,
      error: "Method not implemented",
    }),
  /**
   * Update a user's details
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/users/update_user
   */
  updateOne: async ({ id, data }) => {
    const updatedUser = await restAPI.nodeAPI.PUT<UserReturnType>({
      endpoint: `/users/${id}`,
      body: data,
    });

    return {
      data: updatedUser,
    };
  },
  updateMany: () =>
    Promise.reject({
      success: false,
      error: "Method not implemented",
    }),
  /**
   * Delete a single user
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/users/delete_user
   */
  deleteOne: ({ id }) =>
    restAPI.nodeAPI.DELETE({
      endpoint: `/users/${id}`,
    }),
  deleteMany: () =>
    Promise.reject({
      success: false,
      error: "Method not implemented",
    }),

  /**
   * Export Users list
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/users/export_users
   */
  exportList: ({
    filters,
    sortBy,
    sortDirection,
  }: GetListRequestType & { period?: string }) => {
    return restAPI.nodeAPI.POST<ReportWithDownloadV2Type>({
      endpoint: "/users/export",
      body: {
        sort_by: sortBy,
        sort_direction: sortDirection,
        filter: getFilterObject(filters),
      },
    });
  },

  roles: {
    /**
     * Get a list of roles the user belongs to
     * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/users/list_user_roles
     */
    getList: ({ userId }) =>
      restAPI.nodeAPI.GET<MembershipWithGroupType[]>({
        endpoint: `/users/${userId}/roles`,
      }),
  },
  permissions: {
    /**
     * Get all the configured permissions for a user
     * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/users/get_user_permission
     */
    getMany: ({ userId }) =>
      restAPI.nodeAPI.GET<UserPermissionType[]>({
        endpoint: `/users/${userId}/permissions`,
      }),
    /**
     * Given a list of permissions, get a permission + boolean record back
     * indicating all the permissions of the ones provided that the user has
     * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/users/get_user_permission_decision
     */
    getList: ({ userId }) =>
      restAPI.nodeAPI.POST<Record<string, boolean>>({
        endpoint: `/users/${userId}/permissions/decision`,
      }),
  },
};

export interface UsersAdapterResourceServiceType
  extends Omit<
    ResourceAdapterType<UserReturnType>,
    "getList" | "getOne" | "createOne" | "updateOne"
  > {
  getList: (
    props: GetListRequestType<FilterObjectType<UserWithGroupsType>>
  ) => Promise<GetListResponseType<UserWithGroupsType>>;
  getOne: (
    params: GetOneRequestType
  ) => Promise<GetOneResponseType<UserReturnType>>;
  myself: {
    getOne: () => Promise<GetOneResponseType<UserWithCompaniesType>>;
  };
  createOne: (
    props: UsersCreateOneParamsType
  ) => Promise<CreateOneResponseType<UserReturnType>>;
  updateOne: (
    props: UsersUpdateOneParamsType
  ) => Promise<UpdateOneResponseType<UserReturnType>>;
  roles: {
    getList: ({
      userId,
    }: {
      userId: string;
    }) => Promise<MembershipWithGroupType[]>;
  };
  permissions: {
    getMany: ({ userId }: { userId: string }) => Promise<UserPermissionType[]>;
    getList: ({
      userId,
    }: {
      userId: string;
      permissions: string[];
    }) => Promise<Record<string, boolean>>;
  };
}
